import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { IntlShape, useIntl } from 'react-intl';
import {
  Booking,
  book as postBook,
  getAllBookings,
  BookRequest,
} from 'src/api/bookings';
import { Colaborator } from 'src/api/colaborators';
import { Customer } from 'src/api/customers';
import { Service } from 'src/api/services';
import { useCustomers } from '../final-customers/useCustomers';
import { useCenters } from '../centers/useCenters';
import { useServices } from '../services/useServices';
import { useColaborators } from '../colaborators/useColaborators';
import { Center } from 'src/api/booking/data-contracts';
import { getUserData } from 'src/api/auth';

export interface UseBookingsResult {
  bookingsQuery: QueriedData<Booking[]>;
  book: (bookRequest: BookRequest) => void;
}

export type AggregatedBooking = {
  center: Center;
  service: Service;
  customer: Customer;
  colaborator: Colaborator;
} & Booking;
export interface UseAggregatedBookingsResult {
  bookingsQuery: UseQueryResult<AggregatedBooking[]>;
}

export const defaultMutationErrorHandler = (intl: IntlShape) => () => {
  showErrorNotification('unexpected-error-message', intl);
};
export function useBookings(): UseBookingsResult {
  const user = getUserData();

  const query = useQuery({
    queryFn: getAllBookings,
    queryKey: ['bookings'],
  });

  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: book } = useMutation({
    mutationFn: postBook,
    onSuccess: () => {
      showSuccessNotification('booking-create-success-message', intl);
      queryClient.invalidateQueries(['bookings']);
    },
    onError: () => {
      showErrorNotification('unexpected-error-message', intl);
    },
  });

  return { bookingsQuery: query, book };
}

export function useAggregatedBookings(): UseAggregatedBookingsResult {
  const { bookingsQuery } = useBookings();
  const { customersQuery } = useCustomers();
  const { centersQuery } = useCenters();
  const { servicesQuery } = useServices();
  const { colaboratorsQuery } = useColaborators();

  const query = useQuery({
    queryKey: [
      'bookings',
      'final-customers',
      'services',
      'colaborators',
      'centers',
    ],
    queryFn: () => {
      if (
        !bookingsQuery.data ||
        !customersQuery.data ||
        !centersQuery.data ||
        !servicesQuery.data ||
        !colaboratorsQuery.data
      ) {
        return [];
      }

      return bookingsQuery.data.map(
        (b) =>
          ({
            ...b,
            center: centersQuery.data?.centers.find((c) => c.id === b.centerID),
            service: servicesQuery.data?.find((s) => s.id === b.serviceID),
            colaborator: colaboratorsQuery.data?.find(
              (c) => c.id === b.colaboratorID,
            ),
            customer: customersQuery.data?.find((c) => c.id === b.customerID),
          } as AggregatedBooking),
      );
    },
    enabled:
      !bookingsQuery.isFetching &&
      !customersQuery.isFetching &&
      !centersQuery.isFetching &&
      !servicesQuery.isFetching &&
      !colaboratorsQuery.isFetching,
  });

  return { bookingsQuery: query };
}
